import {
  UNIT_IMPORT_FIELD_LABELS,
  UNIT_IMPORT_NUMERIC_FIELDS,
  UNIT_IMPORT_PRICE_FIELDS,
  UNIT_TYPE_LABELS,
  UNITS_IMPORT_ACTION_LABELS,
  UNITS_IMPORT_MAX_CHARS,
  type UnitImportField,
  type UnitsImportAction,
  type UnitsImportPreview,
  type UnitsImportPreviewRow,
  type UnitType,
} from '@performance-crm/shared';
import { Loader2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { CURRENCY_SYMBOL, fmt } from '@/lib/format';
import { useAdminProjects, useImportUnits, usePreviewUnitsImport } from '@/lib/queries';
import { fileToCsv } from '@/lib/spreadsheet';

const ACTION_TONE: Record<UnitsImportAction, 'success' | 'primary' | 'muted' | 'danger'> = {
  CREAR: 'success',
  ACTUALIZAR: 'primary',
  SIN_CAMBIOS: 'muted',
  OMITIR: 'muted',
  ERROR: 'danger',
};
/** Filas que se pintan en la vista previa (todas se procesan igual). */
const PREVIEW_ROWS = 300;
const decimals = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 2 });

type RowFilter = 'todas' | 'errores' | 'precios' | 'cambios';
const ROW_FILTER_LABELS: Record<RowFilter, string> = {
  todas: 'Todas las filas',
  errores: 'Solo errores',
  precios: 'Solo cambios de precio o moneda',
  cambios: 'Solo lo que se crea o cambia',
};

const touchesPrice = (row: UnitsImportPreviewRow) =>
  row.action === 'ACTUALIZAR' && UNIT_IMPORT_PRICE_FIELDS.some((f) => row.changes.includes(f));
/** Primero lo que hay que revisar (errores, luego cambios de precio o moneda); el resto en el orden del archivo. */
const priority = (row: UnitsImportPreviewRow) =>
  row.action === 'ERROR' ? 0 : touchesPrice(row) ? 1 : 2;

/** Valor ya interpretado tal como se mostrará: tipo y moneda con su nombre, cifras con separador de miles. */
function show(field: UnitImportField, v: unknown): string {
  if (v === null) return '—';
  if (v === undefined) return '';
  if (field === 'unitType') return UNIT_TYPE_LABELS[v as UnitType] ?? String(v);
  if (field === 'currency') return CURRENCY_SYMBOL[v as string] ?? String(v);
  if (typeof v === 'number') return decimals.format(v);
  return String(v);
}

function Cell({ row, field }: { row: UnitsImportPreviewRow; field: UnitImportField }) {
  const after = row.values[field];
  const numeric = UNIT_IMPORT_NUMERIC_FIELDS.includes(field);
  if (row.current && row.changes.includes(field))
    return (
      <td className={`px-2 py-1 ${numeric ? 'text-right' : ''}`}>
        <span className="text-muted-foreground line-through">
          {show(field, row.current[field])}
        </span>
        <span className="mx-1">→</span>
        <span className="font-medium">{show(field, after)}</span>
      </td>
    );
  return (
    <td
      className={`px-2 py-1 ${numeric ? 'text-right' : ''} ${after === undefined ? 'text-muted-foreground' : ''}`}
      title={after === undefined ? 'Sin dato en el archivo: se conserva lo guardado' : undefined}
    >
      {show(field, after)}
    </td>
  );
}

function detail(row: UnitsImportPreviewRow): string {
  if (row.error) return row.error;
  const n = row.changes.length;
  switch (row.action) {
    case 'OMITIR':
      return n
        ? `Ya existe · ${n} dato${n === 1 ? '' : 's'} distinto${n === 1 ? '' : 's'} en el archivo`
        : 'Ya existe, sin cambios';
    case 'SIN_CAMBIOS':
      return 'Ya existe y el archivo trae lo mismo';
    case 'ACTUALIZAR':
      return `Cambia: ${row.changes.map((c) => UNIT_IMPORT_FIELD_LABELS[c].toLowerCase()).join(', ')}`;
    default:
      return '';
  }
}

/**
 * Carga masiva de unidades desde Excel o CSV con vista previa interpretada (ux-comercial-19, ux-admin-22): la tabla
 * muestra lo que se va a guardar (cifras ya leídas, moneda efectiva, antes → después en las existentes, errores por
 * fila) y pisar precios o monedas exige marcar la confirmación. «Actualizar existentes» viene apagado.
 */
export function UnitsImportTab({
  companyId,
  initialProjectId,
}: {
  companyId: string;
  initialProjectId: string;
}) {
  const { data: projects } = useAdminProjects(companyId);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [csv, setCsv] = useState('');
  const [decimal, setDecimal] = useState<'.' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  // Cambia tras importar para vaciar el <input type="file"> y poder volver a subir un archivo con el mismo nombre.
  const [fileKey, setFileKey] = useState(0);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [confirmPrices, setConfirmPrices] = useState(false);
  const [preview, setPreview] = useState<UnitsImportPreview | null>(null);
  const [rowFilter, setRowFilter] = useState<RowFilter>('todas');
  const previewMutation = usePreviewUnitsImport();
  const mutation = useImportUnits();
  const toast = useToast();
  const effective = projectId || projects?.[0]?.id || '';

  const loadFile = async (f: File, sheet?: string) => {
    setReading(true);
    try {
      const read = await fileToCsv(f, sheet);
      if (!read.csv.trim()) throw new Error('El archivo está vacío');
      if (read.csv.length > UNITS_IMPORT_MAX_CHARS)
        throw new Error(
          `El archivo es demasiado grande (${(read.csv.length / 1_000_000).toFixed(1)} MB de texto; el máximo es ${UNITS_IMPORT_MAX_CHARS / 1_000_000} MB): divide la lista en varios archivos.`,
        );
      setCsv(read.csv);
      setDecimal(read.decimal);
      setFile(f);
      setSheetName(read.sheetName);
      setSheetNames(read.sheetNames);
      setRowFilter('todas');
      mutation.reset();
    } catch (err) {
      toast.error('No se pudo leer el archivo', (err as Error).message);
    } finally {
      setReading(false);
    }
  };

  // Cualquier cambio (archivo, hoja, proyecto o «actualizar existentes») rehace la vista previa en el servidor.
  useEffect(() => {
    setConfirmPrices(false);
    if (!csv || !effective) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    previewMutation
      .mutateAsync({
        projectId: effective,
        csv,
        updateExisting,
        ...(decimal ? { decimal } : {}),
      })
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setPreview(null);
          toast.error('No se pudo interpretar el archivo', err.message);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csv, decimal, effective, updateExisting]);

  const needsConfirm = !!preview && preview.priceChanges > 0;
  const nothingToDo = !!preview && preview.toCreate + preview.toUpdate === 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!preview) return;
    try {
      const r = await mutation.mutateAsync({
        projectId: effective,
        csv,
        updateExisting,
        confirmPriceChanges: confirmPrices,
        ...(decimal ? { decimal } : {}),
      });
      toast.success(
        `Carga lista: ${r.created} creadas, ${r.updated} actualizadas${r.unchanged ? `, ${r.unchanged} sin cambios` : ''}`,
        r.errors.length ? `${r.errors.length} filas con error no se guardaron` : undefined,
      );
      setCsv('');
      setDecimal(null);
      setFile(null);
      setFileKey((k) => k + 1);
      setSheetName(null);
      setSheetNames([]);
      setPreview(null);
    } catch (err) {
      toast.error('No se pudo importar', (err as Error).message);
    }
  };
  const recognized = (preview?.columns ?? [])
    .map((c) => c.field)
    .filter((f): f is UnitImportField => f !== 'code');
  // La moneda efectiva se muestra siempre que haya precios, aunque el archivo no traiga columna Moneda.
  const showsCurrency =
    (recognized.includes('priceList') || recognized.includes('priceSale')) &&
    !recognized.includes('currency');
  const fields: UnitImportField[] = showsCurrency ? [...recognized, 'currency'] : recognized;
  const visibleRows = useMemo(() => {
    const all = preview?.rows ?? [];
    switch (rowFilter) {
      case 'errores':
        return all.filter((r) => r.action === 'ERROR');
      case 'precios':
        return all.filter(touchesPrice);
      case 'cambios':
        return all.filter((r) => r.action === 'CREAR' || r.action === 'ACTUALIZAR');
      default:
        return [...all].sort((a, b) => priority(a) - priority(b) || a.row - b.row);
    }
  }, [preview, rowFilter]);
  const shownRows = visibleRows.slice(0, PREVIEW_ROWS);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Carga masiva de unidades (Excel o CSV)</CardTitle>
        <CardDescription>
          Columnas{' '}
          <code>
            codigo, tipo, tipologia, piso, dormitorios, banos, area, precio_lista, precio, moneda,
            torre
          </code>{' '}
          (o sus equivalentes en inglés), con la cabecera en la primera fila con datos. Crea las
          unidades nuevas por código y, solo si lo pides, actualiza las existentes; las torres /
          etapas nuevas se crean al vuelo. Las cifras se leen tal como vienen en las listas de
          precios («S/ 350,000», «US$ 152,000.00», «45,5 m2») y la moneda escrita en la celda es la
          que se guarda; un texto donde debería ir un número («consultar») deja la fila en error y
          no toca nada.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ui-project">Proyecto</Label>
              <Select
                id="ui-project"
                value={effective}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ui-file">Archivo (.xlsx, .xls o .csv)</Label>
              <Input
                key={fileKey}
                id="ui-file"
                type="file"
                accept=".xlsx,.xlsm,.xls,.ods,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                data-testid="units-csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void loadFile(f);
                }}
              />
              {file && sheetNames.length > 1 ? (
                <div className="space-y-1">
                  <Label htmlFor="ui-sheet" className="text-xs">
                    Hoja del Excel ({sheetNames.length} hojas)
                  </Label>
                  <Select
                    id="ui-sheet"
                    className="h-9"
                    value={sheetName ?? ''}
                    onChange={(e) => void loadFile(file, e.target.value)}
                    data-testid="units-sheet"
                  >
                    {sheetNames.map((sh) => (
                      <option key={sh} value={sh}>
                        {sh}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : (
                file && (
                  <p className="text-muted-foreground text-xs">
                    {file.name}
                    {sheetName ? ` · hoja "${sheetName}"` : ''}
                  </p>
                )
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              className="h-4 w-4"
              data-testid="units-update-existing"
            />
            Actualizar las unidades que ya existen (precios, moneda, tipología, piso, áreas, torre)
          </label>
          {(reading || previewMutation.isPending) && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />{' '}
              {reading ? 'Leyendo el archivo…' : 'Interpretando las filas…'}
            </p>
          )}
          {preview && (
            <div className="space-y-3" data-testid="units-import-preview">
              <p className="text-sm">
                <span className="font-medium">{fmt.number(preview.total)} filas</span> ·{' '}
                <span className="text-success">{preview.toCreate} se crean</span> ·{' '}
                <span className="text-primary">{preview.toUpdate} se actualizan</span>
                {preview.unchanged > 0 ? ` · ${preview.unchanged} sin cambios` : ''} ·{' '}
                {preview.toSkip} se omiten
                {preview.toSkip > 0 && !updateExisting ? ' (ya existen)' : ''} ·{' '}
                <span className={preview.errors ? 'text-danger' : ''}>
                  {preview.errors} con error
                </span>
              </p>
              {preview.ignoredColumns.length > 0 && (
                <p className="text-muted-foreground text-xs" data-testid="units-ignored-columns">
                  Se ignoraron estas columnas porque no se reconocen:{' '}
                  {preview.ignoredColumns.join(', ')}.
                </p>
              )}
              {needsConfirm && (
                <label
                  className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30"
                  data-testid="units-import-confirm"
                >
                  <input
                    type="checkbox"
                    checked={confirmPrices}
                    onChange={(e) => setConfirmPrices(e.target.checked)}
                    className="mt-0.5 h-4 w-4"
                  />
                  <span>
                    Esta carga cambia el precio o la moneda de{' '}
                    <b>
                      {preview.priceChanges} unidad{preview.priceChanges === 1 ? '' : 'es'}
                    </b>{' '}
                    que ya existen (revisa el antes → después en la tabla). Sí, reemplazar los
                    precios guardados por los del archivo.
                  </span>
                </label>
              )}
              {nothingToDo && (
                <p className="text-muted-foreground text-sm">
                  No hay nada que guardar con esta configuración
                  {preview.toSkip > 0 && !updateExisting
                    ? ': todas las unidades del archivo ya existen; marca «Actualizar las unidades que ya existen» si quieres pisar sus datos'
                    : preview.unchanged > 0
                      ? ': las unidades del archivo ya tienen los mismos datos'
                      : preview.errors > 0
                        ? ': corrige las filas con error y vuelve a subir el archivo'
                        : ''}
                  .
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Label htmlFor="ui-row-filter" className="text-xs">
                  Mostrar
                </Label>
                <Select
                  id="ui-row-filter"
                  className="h-8 w-auto text-xs"
                  value={rowFilter}
                  onChange={(e) => setRowFilter(e.target.value as RowFilter)}
                  data-testid="units-row-filter"
                >
                  {(Object.keys(ROW_FILTER_LABELS) as RowFilter[]).map((k) => (
                    <option key={k} value={k}>
                      {ROW_FILTER_LABELS[k]}
                    </option>
                  ))}
                </Select>
                {rowFilter !== 'todas' && (
                  <span className="text-muted-foreground">
                    {fmt.number(visibleRows.length)} de {fmt.number(preview.rows.length)} filas
                  </span>
                )}
                {rowFilter === 'todas' && visibleRows.length > 0 && (
                  <span className="text-muted-foreground">
                    Primero los errores y los cambios de precio; el resto, en el orden del archivo.
                  </span>
                )}
              </div>
              <div className="max-h-[28rem] overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-muted-foreground sticky top-0 text-left uppercase tracking-wide">
                    <tr>
                      <th className="px-2 py-1">Fila</th>
                      <th className="px-2 py-1">Código</th>
                      <th className="px-2 py-1">Acción</th>
                      {fields.map((f) => (
                        <th
                          key={f}
                          className={`px-2 py-1 ${UNIT_IMPORT_NUMERIC_FIELDS.includes(f) ? 'text-right' : ''}`}
                          title={
                            f === 'currency' && showsCurrency
                              ? 'Moneda con la que se guardará el precio: la escrita en la celda; si no, la de la unidad (US$ en las nuevas)'
                              : undefined
                          }
                        >
                          {UNIT_IMPORT_FIELD_LABELS[f]}
                        </th>
                      ))}
                      <th className="px-2 py-1">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.map((row) => (
                      <tr
                        key={row.row}
                        className={`border-t ${row.action === 'ERROR' ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}
                        data-testid="units-preview-row"
                      >
                        <td className="text-muted-foreground px-2 py-1">{row.row}</td>
                        <td className="px-2 py-1 font-medium">{row.code ?? '—'}</td>
                        <td className="px-2 py-1">
                          <Badge tone={ACTION_TONE[row.action]}>
                            {UNITS_IMPORT_ACTION_LABELS[row.action]}
                          </Badge>
                        </td>
                        {fields.map((f) => (
                          <Cell key={f} row={row} field={f} />
                        ))}
                        <td
                          className={`px-2 py-1 ${row.action === 'ERROR' ? 'text-danger' : 'text-muted-foreground'}`}
                        >
                          {detail(row)}
                        </td>
                      </tr>
                    ))}
                    {shownRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={4 + fields.length}
                          className="text-muted-foreground px-2 py-4 text-center"
                        >
                          Ninguna fila cumple este filtro.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {visibleRows.length > PREVIEW_ROWS && (
                <p className="text-muted-foreground text-xs">
                  Se muestran {PREVIEW_ROWS} de {fmt.number(visibleRows.length)} filas:{' '}
                  {fmt.number(visibleRows.length - PREVIEW_ROWS)} no se muestran (usa «Mostrar» para
                  verlas por grupo); todas se procesan al importar.
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                !preview ||
                !effective ||
                nothingToDo ||
                (needsConfirm && !confirmPrices) ||
                mutation.isPending ||
                previewMutation.isPending
              }
              data-testid="units-import"
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}{' '}
              Importar
              {preview && !nothingToDo
                ? ` (${preview.toCreate + preview.toUpdate} unidad${preview.toCreate + preview.toUpdate === 1 ? '' : 'es'})`
                : ''}
            </Button>
          </div>
          {mutation.data && (
            <div className="rounded-md border p-3 text-sm" data-testid="units-import-result">
              <p>
                {mutation.data.total} filas · {mutation.data.created} creadas ·{' '}
                {mutation.data.updated} actualizadas
                {mutation.data.unchanged > 0
                  ? ` · ${mutation.data.unchanged} sin cambios`
                  : ''} · {mutation.data.skipped} omitidas · {mutation.data.errors.length} con error
              </p>
              {mutation.data.errors.length > 0 && (
                <ul className="text-danger mt-1 text-xs">
                  {mutation.data.errors.slice(0, 20).map((e) => (
                    <li key={e.row}>
                      Fila {e.row}
                      {e.code ? ` (${e.code})` : ''}: {e.message}
                    </li>
                  ))}
                  {mutation.data.errors.length > 20 && (
                    <li>…y {mutation.data.errors.length - 20} filas más con error.</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
